import { useMemo } from "react";
import BarChart from "@/components/Chart/BarChart";
import LineChart from "@/components/Chart/LineChart";
import { sysColors } from "@/assets/theme/colors";
import { ChartCard, EmptyChartMessage } from "../../_components/ChartShells";
import type { CareerServicesCategoryStat, CareerServicesModeStat } from "../queries";
import type { CareerServicesDayTotal } from "../_utils";
import { buildCategoryBarData, buildModeBarData, buildVolumeTrendData } from "../_utils";

type Props = {
  byMode: CareerServicesModeStat[];
  categories: CareerServicesCategoryStat[];
  days: CareerServicesDayTotal[];
  loading?: boolean;
};

export default function CareerCharts({ byMode, categories, days, loading = false }: Props) {
  const modeBarData = useMemo(() => buildModeBarData(byMode), [byMode]);
  const categoryBarData = useMemo(() => buildCategoryBarData(categories), [categories]);
  const trend = useMemo(() => buildVolumeTrendData(days), [days]);
  // Vertical bars need ~30px per row; clamp so full and filtered states both breathe.
  const modeChartHeight = Math.min(460, Math.max(225, modeBarData.length * 30 + 60));
  const categoryChartHeight = Math.min(460, Math.max(225, categoryBarData.length * 30 + 60));

  return (
    <div className="grid gap-4 md:grid-cols-2 mt-8" data-testid="career-services-charts">
      <BarChart
        data={modeBarData}
        bars={[{ dataKey: "Interactions", name: "Interactions", color: sysColors["sys-brand-primary"] }]}
        title="Interactions by mode"
        subtitle="Career Services interactions in range."
        loading={loading}
        layout="vertical"
        height={modeChartHeight}
        showValueLabels
        emptyLabel="No interactions match these filters."
      />

      <BarChart
        data={categoryBarData}
        bars={[{ dataKey: "Interactions", name: "Interactions", color: sysColors["sys-brand-primary"] }]}
        title="Interactions by category"
        subtitle="Selected categories in range."
        loading={loading}
        layout="vertical"
        height={categoryChartHeight}
        showValueLabels
        emptyLabel="No interactions match these filters."
      />

      {!loading && trend.rows.length === 0 ? (
        <ChartCard title="Daily interaction volume" testId="career-services-trend-chart">
          <EmptyChartMessage label="No interactions match these filters." />
        </ChartCard>
      ) : (
        <LineChart
          data={trend.rows}
          lines={trend.series}
          title="Daily interaction volume"
          subtitle="Interactions per day in range."
          loading={loading}
          className="md:col-span-2"
        />
      )}
    </div>
  );
}
